import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Select,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useInsuranceProvidersAdmin,
  useCreateInsuranceProvider,
  useUpdateInsuranceProvider,
  useToggleInsuranceProviderActive,
  useDeleteInsuranceProvider,
} from "../../../hooks/admin/useInsuranceProvidersAdmin";
import InsuranceProviderForm, {
  InsuranceProviderFormValues,
} from "../../../components/admin/insurance-providers/InsuranceProviderForm";
import { InsuranceProvider } from "../../../services/admin/insuranceProviders.service";

interface InsuranceProviderFilters {
  status?: "active" | "inactive" | "all";
  states?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function InsuranceProvidersAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    toggleModalOpened,
    { open: openToggleModal, close: closeToggleModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<InsuranceProvider | null>(null);
  const [itemToDelete, setItemToDelete] = useState<InsuranceProvider | null>(
    null
  );
  const [itemToToggle, setItemToToggle] = useState<InsuranceProvider | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<InsuranceProviderFilters>({});
  const [pendingFilters, setPendingFilters] =
    useState<InsuranceProviderFilters>({});
  const [page, setPage] = useState(1);

  const {
    data: providers = [],
    isLoading,
    refetch,
  } = useInsuranceProvidersAdmin();
  const createProvider = useCreateInsuranceProvider();
  const updateProvider = useUpdateInsuranceProvider();
  const toggleActive = useToggleInsuranceProviderActive();
  const deleteProvider = useDeleteInsuranceProvider();

  const filteredData = useMemo(() => {
    let data = providers;

    if (debouncedSearch) {
      const lower = debouncedSearch.toLowerCase();
      data = data.filter(
        (item) =>
          item.name.toLowerCase().includes(lower) ||
          (item.naicNumber || "").toLowerCase().includes(lower) ||
          (item.city || "").toLowerCase().includes(lower) ||
          (item.state || "").toLowerCase().includes(lower) ||
          (item.phoneNumber || "").toLowerCase().includes(lower)
      );
    }

    if (filters.status === "active") {
      data = data.filter((item) => item.isActive);
    } else if (filters.status === "inactive") {
      data = data.filter((item) => !item.isActive);
    }

    return data;
  }, [providers, debouncedSearch, filters]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const hasActiveFilters = !!(filters.status && filters.status !== "all");

  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: InsuranceProvider) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: InsuranceProviderFormValues) => {
    if (editing) {
      await updateProvider.mutateAsync({
        id: editing.id,
        ...values,
      });
    } else {
      await createProvider.mutateAsync(values);
    }
    closeFormModal();
    setEditing(null);
  };

  const handleDelete = (item: InsuranceProvider) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteProvider.mutateAsync(itemToDelete.id);
      closeDeleteModal();
      setItemToDelete(null);
    }
  };

  const handleToggleActive = (item: InsuranceProvider) => {
    setItemToToggle(item);
    openToggleModal();
  };

  const confirmToggle = async () => {
    if (itemToToggle) {
      await toggleActive.mutateAsync({
        id: itemToToggle.id,
        isActive: itemToToggle.isActive,
      });
      closeToggleModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Insurance Providers</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage insurance companies and payers
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, NAIC, city, state, or phone..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status && filters.status !== "all" && (
                      <Badge
                        variant="light"
                        color={filters.status === "active" ? "green" : "red"}
                      >
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} provider
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Insurance Providers ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No insurance providers match the current filters"
                    : "No insurance providers found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Provider Name</Table.Th>
                      <Table.Th style={{ width: 120 }}>NAIC</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th style={{ width: 140 }}>City</Table.Th>
                      <Table.Th style={{ width: 80 }}>State</Table.Th>
                      <Table.Th style={{ width: 100 }}>ZIP</Table.Th>
                      <Table.Th style={{ width: 140 }}>Phone</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 150 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {item.naicNumber || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" lineClamp={1}>
                            {item.mailingAddress || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.city || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.state || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {item.zipCode || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {item.phoneNumber || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "orange" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editing ? "Edit Insurance Provider" : "Add New Insurance Provider"
          }
          size="md"
          closeOnClickOutside={false}
        >
          <InsuranceProviderForm
            initialValues={editing || undefined}
            existingProviders={providers}
            onSubmit={handleSubmit}
            onCancel={closeFormModal}
            isSubmitting={createProvider.isPending || updateProvider.isPending}
            submitLabel={editing ? "Update" : "Create"}
          />
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Insurance Providers"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status:
                    value === "all"
                      ? undefined
                      : (value as "active" | "inactive"),
                })
              }
              clearable={false}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => setPendingFilters({})}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={toggleModalOpened}
          onClose={closeToggleModal}
          title={
            itemToToggle?.isActive
              ? "Deactivate Insurance Provider"
              : "Activate Insurance Provider"
          }
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"}{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivating this provider will remove it from selection
                dropdowns throughout the system.
              </Text>
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeToggleModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggle}
                loading={toggleActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Insurance Provider"
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete{" "}
              <strong>{itemToDelete?.name}</strong>?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteProvider.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
